// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — components/CClassTribSearchCombobox.tsx
//
//  Escolhe a Classificação Tributária IBS/CBS (cClassTrib) por código ou nome,
//  no lugar do Select de ~160 itens sem busca. Busca em memória sobre o combo
//  em cache (GET /api/cadastros/fiscal/cclass-trib/combo via cClassTribService).
// ════════════════════════════════════════════════════════════════════════════

import { EntityCombobox } from "@/components/EntityCombobox";
import { cClassTribService } from "@/features/fiscal/services/fiscalService";
import type { CClassTribComboDto } from "@/features/fiscal/types";

let cache: { em: number; itens: Promise<CClassTribComboDto[]> } | null = null;
const carregar = () => {
  if (!cache || Date.now() - cache.em > 5 * 60_000)
    cache = { em: Date.now(), itens: cClassTribService.combo().catch((e) => { cache = null; throw e; }) };
  return cache.itens;
};

const normalizar = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

async function buscar(termo: string): Promise<CClassTribComboDto[]> {
  const todas = await carregar();
  const t = normalizar(termo.trim());
  return (t
    ? todas.filter((c) => c.codCClassTrib.startsWith(t) || normalizar(c.nome ?? "").includes(t))
    : todas).slice(0, 60);
}

async function obterPorId(id: number): Promise<CClassTribComboDto> {
  const c = (await carregar()).find((x) => x.id === id);
  if (!c) throw new Error("Classificação tributária não encontrada.");
  return c;
}

interface Props {
  value?: number | null;
  onChange?: (id: number | null) => void;
  onSelect?: (item: CClassTribComboDto | null) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  permitirLimpar?: boolean;
}

export function CClassTribSearchCombobox({ placeholder = "Selecionar classificação...", ...props }: Props) {
  return (
    <EntityCombobox<CClassTribComboDto>
      {...props}
      queryKey="cclass-trib-lookup"
      buscar={buscar}
      obterPorId={obterPorId}
      getId={(c) => c.id}
      getCodigo={(c) => c.codCClassTrib}
      getDescricao={(c) => c.nome}
      minCaracteres={0}
      placeholder={placeholder}
      placeholderBusca="Buscar por código ou nome..."
    />
  );
}
